import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ShieldOff, ChevronLeft } from 'lucide-react';
import { useTenant } from '../context/TenantContext';

/**
 * Maps URL prefixes to the top-level module keys an admin can disable in
 * Organization Settings (MANAGEABLE_MODULES in TenantSettings.tsx).
 *
 * Order matters: the first matching rule wins. Always-allowed paths
 * (login, the org profile itself, public assets) are listed in ALWAYS_ALLOW.
 */
const ALWAYS_ALLOW = [
  '/',                 // landing/home (cannot be blocked or user has no escape)
  '/login',
  '/logout',
  '/profile',
  '/tenants/tree',     // user must always be able to see their own org tree
  '/tenants/tickets',  // tickets are the channel parents use to reach this org
  '/federations',      // federations are how Root Entities collaborate
  '/evidence',         // evidence vault is core for Client archetypes
  '/trust-center',     // own Trust Center management
  '/playbooks',        // playbook library is core for Consulting/Audit firms
  '/llm-routing',      // every tenant needs to set inference compute
  '/partner-ledger',   // attribution transparency for both parties
];

const MODULE_ROUTE_RULES: Array<{ module: string; test: (p: string) => boolean }> = [
  { module: 'dashboard-section', test: (p) => p.startsWith('/dashboards') || p.startsWith('/dashboard/') || p.startsWith('/analytics') },
  { module: 'process-module',    test: (p) => p.startsWith('/instances') || p.startsWith('/tasks') || p.startsWith('/monitor') || p.startsWith('/metrics') || p.startsWith('/agents') || p.startsWith('/process') },
  { module: 'risk-module',       test: (p) => p.startsWith('/risks') },
  { module: 'controls-module',   test: (p) => p.startsWith('/controls') },
  { module: 'compliance-module', test: (p) => p.startsWith('/compliances') || p.startsWith('/standards') || p.startsWith('/objective') || p.startsWith('/assessment') || p.startsWith('/policy') || p.startsWith('/announcements') || p.startsWith('/ticket') || p.startsWith('/impactarea') || p.startsWith('/surveys') || p.startsWith('/create-survey') || p.startsWith('/bankQuestions') || p.startsWith('/rcsa-compliance') },
  { module: 'enterprise',        test: (p) => p.startsWith('/ea') },
  { module: 'iams',              test: (p) => p.startsWith('/iams') },
  { module: 'catalogs',          test: (p) => p.startsWith('/catalogs') },
  { module: 'data-inventory',    test: (p) => p.startsWith('/data-inventory') || p.startsWith('/assets') },
  { module: 'sup-compliance',    test: (p) => p.startsWith('/sup-compliance') || p.startsWith('/demo-partner') },
  { module: 'document-management', test: (p) => p.startsWith('/documents') },
  { module: 'reporting',         test: (p) => p.startsWith('/reporting') },
  { module: 'organizations',     test: (p) => p.startsWith('/tenants') },
  { module: 'user-settings',     test: (p) => p.startsWith('/user') || p.startsWith('/permission') || p.startsWith('/organization-chart') },
  { module: 'configuration',     test: (p) => p.startsWith('/configuration') || p.startsWith('/system') },
];

interface Props {
  children: React.ReactNode;
}

const ModuleGate: React.FC<Props> = ({ children }) => {
  const { currentTenant, isLoading } = useTenant();
  const location = useLocation();
  const path = location.pathname;

  if (isLoading) return <>{children}</>;

  const disabled = new Set(currentTenant?.disabledModules ?? []);
  if (disabled.size === 0) return <>{children}</>;

  if (ALWAYS_ALLOW.includes(path)) return <>{children}</>;

  const rule = MODULE_ROUTE_RULES.find((r) => r.test(path));
  if (!rule) return <>{children}</>;
  if (!disabled.has(rule.module)) return <>{children}</>;

  return <ModuleBlockedPage module={rule.module} />;
};

const ModuleBlockedPage: React.FC<{ module: string }> = ({ module }) => {
  const navigate = useNavigate();
  return (
    <div className="flex min-h-[70vh] items-center justify-center px-6">
      <div className="max-w-md rounded-3xl border border-slate-200 bg-white p-10 text-center shadow-sm">
        <div className="mx-auto mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full bg-rose-100 text-rose-600">
          <ShieldOff className="h-7 w-7" />
        </div>
        <h1 className="text-xl font-bold text-slate-900">Module unavailable</h1>
        <p className="mt-2 text-sm text-slate-600">
          The <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs">{module}</code> module
          has been disabled for your organization by your parent administrator.
        </p>
        <p className="mt-1 text-xs text-slate-500">
          If you believe this is a mistake, contact your administrator or send a ticket.
        </p>
        <button
          type="button"
          onClick={() => navigate('/')}
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700"
        >
          <ChevronLeft className="h-4 w-4" /> Back to home
        </button>
      </div>
    </div>
  );
};

export default ModuleGate;
